import { useCallback, useMemo, useState } from 'react';
import type { WizardStep, WizardCompletion } from './Wizard';

export interface UseWizardOptions {
  steps: WizardStep[];
  activeKey?: string;
  onActiveKeyChange?: (key: string) => void;
  defaultActiveKey?: string;
}

export interface UseWizardResult {
  activeKey: string;
  activeIndex: number;
  steps: WizardStep[];
  /** Per-step completion, resolved once from each step's `isCompleted`. */
  completedByIndex: boolean[];
  completion: WizardCompletion;
  isFirstStep: boolean;
  isLastStep: boolean;
  /** Whether the current step is complete (drives a "Continue" gate). */
  canGoNext: boolean;
  goTo: (key: string) => void;
  goNext: () => void;
  goBack: () => void;
  /**
   * Whether navigating to a given step is allowed. A step is reachable when it
   * is not `disabled` and every preceding non-disabled step is completed.
   */
  canNavigateTo: (key: string) => boolean;
}

/**
 * Headless wizard logic: step resolution, completion aggregation and
 * navigation. Owns no UI, so consumers can render the indicator and action bar
 * however they like (see `WizardSteps` for a ready-made indicator). Works
 * controlled (pass `activeKey` + `onActiveKeyChange`) or uncontrolled.
 */
export const useWizard = ({
  steps,
  activeKey: controlledKey,
  onActiveKeyChange,
  defaultActiveKey,
}: UseWizardOptions): UseWizardResult => {
  const [internalKey, setInternalKey] = useState(
    defaultActiveKey ?? steps[0]?.key ?? ''
  );
  const activeKey = controlledKey ?? internalKey;
  const activeIndex = steps.findIndex((s) => s.key === activeKey);

  const setActiveKey = useCallback(
    (key: string) => {
      if (controlledKey === undefined) setInternalKey(key);
      onActiveKeyChange?.(key);
    },
    [controlledKey, onActiveKeyChange]
  );

  const completedByIndex = useMemo<boolean[]>(
    () =>
      steps.map((step, stepIndex) => {
        if (typeof step.isCompleted === 'function') {
          return step.isCompleted({
            stepIndex,
            activeIndex,
            activeKey,
            stepCount: steps.length,
          });
        }
        return !!step.isCompleted;
      }),
    [steps, activeIndex, activeKey]
  );

  const lastEnabledIndex = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (!steps[i].disabled) return i;
    }
    return -1;
  }, [steps]);

  const completion = useMemo<WizardCompletion>(
    () => ({
      activeKey,
      isLastStep: activeIndex === lastEnabledIndex,
      currentStepCompleted: !!completedByIndex[activeIndex],
      allStepsCompleted: steps.every(
        (s, i) => s.disabled || completedByIndex[i]
      ),
    }),
    [activeKey, activeIndex, lastEnabledIndex, completedByIndex, steps]
  );

  const canNavigateTo = useCallback(
    (key: string) => {
      const targetIndex = steps.findIndex((s) => s.key === key);
      if (targetIndex < 0 || steps[targetIndex].disabled) return false;
      if (targetIndex <= activeIndex) return true;
      for (let i = 0; i < targetIndex; i++) {
        if (!steps[i].disabled && !completedByIndex[i]) return false;
      }
      return true;
    },
    [steps, activeIndex, completedByIndex]
  );

  const goTo = useCallback(
    (key: string) => {
      if (canNavigateTo(key)) setActiveKey(key);
    },
    [canNavigateTo, setActiveKey]
  );

  const goNext = useCallback(() => {
    for (let i = activeIndex + 1; i < steps.length; i++) {
      if (!steps[i].disabled) {
        setActiveKey(steps[i].key);
        return;
      }
    }
  }, [activeIndex, steps, setActiveKey]);

  const goBack = useCallback(() => {
    for (let i = activeIndex - 1; i >= 0; i--) {
      if (!steps[i].disabled) {
        setActiveKey(steps[i].key);
        return;
      }
    }
  }, [activeIndex, steps, setActiveKey]);

  return {
    activeKey,
    activeIndex,
    steps,
    completedByIndex,
    completion,
    isFirstStep: activeIndex <= 0,
    isLastStep: completion.isLastStep,
    canGoNext: completion.currentStepCompleted,
    goTo,
    goNext,
    goBack,
    canNavigateTo,
  };
};
